import * as DOMPurify from "dompurify";
import * as React from "react";
import { TOP_NAV_HEIGHT } from "../NavigationUtils/NavigationUtils";
import { throttle } from "../throttle/throttle";

interface SanitizeConfig {
  allowUnsanitized: boolean;
  allowExternalImages: boolean;
  allowAnchorTarget: boolean;
}
type URLMatcher = (url: URL) => boolean;
type URLStringMatcher = (urlStr: string) => boolean;

/**
 * These tags are always forbidden in setInnerHtmlSanitized.
 * See http://go/dompurify-security
 * The default allowed tags are the set of tags defined at
 * https://github.com/cure53/DOMPurify/blob/main/src/tags.js
 * excluding FORBIDDEN_TAGS.
 */
const FORBIDDEN_TAGS: Readonly<string[]> = ["style"];
const KAGGLE_DOMAIN_RE = /^(www\.|staging\.|admin\.)?kaggle\.com$/i;
const EXTERNAL_DOMAIN_REL_VALUES: Readonly<string[]> = [
  "noreferrer",
  "nofollow",
];
const ALLOWED_IMG_PREFIXES: Readonly<string[]> = [
  "i.postimg.cc/",
  "i.imgur.com/",
  "pbs.twimg.com/",
  "i.ibb.co/",
  "raw.githubusercontent.com/",
  "user-images.githubusercontent.com/",
  "storage.googleapis.com/",
  "www.kaggleusercontent.com/",
  "www.googleapis.com/download/storage/v1",
];
const DEFAULT_SANITIZE_CONFIG: Readonly<SanitizeConfig> = {
  allowUnsanitized: false,
  allowExternalImages: true,
  allowAnchorTarget: false,
};

// Returns "noreferrer nofollow" if the href is an external domain.
export const getRelAttrForHref = (href: string): string | undefined => {
  if (isKaggleUrl(href)) {
    return undefined;
  }
  return EXTERNAL_DOMAIN_REL_VALUES.join(" ");
};

/**
 * Takes a function that matches URL objects and returns a
 * function that matches arbitrary stings - which may or may not
 * be valid URLs. Invalid URLs will return false.
 */
const getUrlStringMatcher =
  (urlFn: URLMatcher): URLStringMatcher =>
  (urlStr: string) => {
    let url: URL;
    try {
      url = new URL(urlStr, window.location.href);
    } catch (_) {
      // Return false for unparseable URL.
      return false;
    }
    return urlFn(url);
  };

const isKaggleDomain = (url: URL) =>
  url.hostname.match(KAGGLE_DOMAIN_RE) !== null;

/**
 * Returns true if the URL is relative or a Kaggle domain.
 */
export const isKaggleUrl = getUrlStringMatcher(isKaggleDomain);

/**
 * Returns true if the URL is relative, a Kaggle domain,
 * or one of the allowed 3rd party image domains.
 */
export const isAllowedImgDomainUrl = getUrlStringMatcher((url: URL) => {
  if (isKaggleDomain(url)) {
    return true;
  }
  const hostAndPath =
    url.hostname.toLowerCase() + url.pathname.toLocaleLowerCase();
  // Do a head match on domain and path.
  return ALLOWED_IMG_PREFIXES.some(prefix => hostAndPath.indexOf(prefix) === 0);
});

/**
 * Adds the attribute rel="noreferrer nofollow" if the anchor
 * element links to a non-Kaggle domain.
 */
export const processAnchorRel = (elem: HTMLAnchorElement) => {
  const href = elem.getAttribute("href");
  if (href !== null && !isKaggleUrl(href)) {
    const currentRel = elem.getAttribute("rel") || "";
    // The same node can be processed multiple times, so don't duplicate values.
    const relVals = new Set([
      ...currentRel.split(/\s+/),
      ...EXTERNAL_DOMAIN_REL_VALUES,
    ]);
    elem.setAttribute("rel", [...relVals].join(" "));
  }
};

let cachedImageIcon: HTMLElement | null = null;
const getImageIcon = () => {
  if (cachedImageIcon !== null) {
    return cachedImageIcon;
  }
  const icon = document.createElement("i");
  icon.setAttribute(
    "class",
    "rmwc-icon google-material-icons markdown-renderer-image-icon"
  );
  const iconText = document.createTextNode("image");
  icon.appendChild(iconText);
  cachedImageIcon = icon;
  return icon;
};

/**
 * Replaces 3rd-party image elements with links.
 */
export const processImgSrc = (elem: HTMLImageElement) => {
  const src = elem.getAttribute("src");
  if (src !== null && !isAllowedImgDomainUrl(src)) {
    // Create a new link that links to the image src.
    const link = document.createElement("a");
    link.setAttribute("href", src);
    link.setAttribute("class", "external-img-link");

    // Add an icon.
    link.appendChild(getImageIcon());

    // Set the text of the link.
    const text = document.createTextNode(src);
    link.appendChild(text);

    // Replace the img element.
    elem.replaceWith(link);
  }
};

/**
 * Type-guard that narrows Element to HTMLAnchorElement
 */
const nodeIsAnchor = (node: Element): node is HTMLAnchorElement =>
  node.nodeType === 1 && node.nodeName === "A";

/**
 * Type-guard that narrows Element to HTMLAnchorElement
 */
const nodeIsImg = (node: Element): node is HTMLImageElement =>
  node.nodeType === 1 && node.nodeName === "IMG";

/**
 * Processes a single HTLM Element, possibly modifying it.
 * Some of the behavior is dictated by SanitizeConfig.
 */
const processElement = (
  currentNode: Element,
  sanitizeConfig: SanitizeConfig
) => {
  if (nodeIsAnchor(currentNode)) {
    processAnchorRel(currentNode);
  } else if (nodeIsImg(currentNode) && !sanitizeConfig.allowExternalImages) {
    processImgSrc(currentNode);
  }
  return currentNode;
};

/**
 * WARNING: Any direct innerHTML changes must be done via this function!
 * Setting innerHTML without sanitizing immediately prior creates a risk for
 * XSS attacks. Never set innerHTML or use dangeroustlySetInnerHTML directly!
 * Sets innerHTML on React element (via dangerouslySetInnerHTML) or DOM Element
 * (via element.innerHTML.)
 *
 * sanitize=false should only be set for content that comes directly from the
 * Kaggle CMS (e.g., pages that use jotforms).
 *
 * Overloads are provided so the return value is typed
 * properly without the need for generics:
 */
export function setInnerHtmlSanitized(
  element: React.ReactElement,
  html: string,
  options?: Partial<SanitizeConfig>
): React.ReactElement;
export function setInnerHtmlSanitized(
  element: Element,
  html: string,
  options?: Partial<SanitizeConfig>
): Element;
export function setInnerHtmlSanitized(
  element: React.ReactElement | Element,
  html: string,
  options?: Partial<SanitizeConfig>
) {
  // Merge options with the default config.
  const config: SanitizeConfig = {
    ...DEFAULT_SANITIZE_CONFIG,
    ...(options || {}),
  };
  const { allowUnsanitized, allowAnchorTarget } = config;

  if (!allowUnsanitized) {
    const processor = (elem: Element) => processElement(elem, config);

    /**
     * Adds a hook to the DOMPurify config for pre-processing elements.
     * This allows for manipulating elements via the DOM api before the
     * sanitization step.
     * https://github.com/cure53/DOMPurify#hooks
     */
    DOMPurify.addHook("beforeSanitizeElements", processor);

    html = DOMPurify.sanitize(html, {
      FORBID_TAGS: [...FORBIDDEN_TAGS],
      ADD_ATTR: allowAnchorTarget ? ["target"] : undefined,
    });

    // Remove the hook when processing is finished.
    DOMPurify.removeHook("beforeSanitizeElements");
  }
  if ("innerHTML" in element) {
    element.innerHTML = html;
    return element;
  } else {
    return React.cloneElement(element, {
      dangerouslySetInnerHTML: { __html: html },
    });
  }
}

// Set all external image links to open in a new tab.
export const processExternalImgLinks = (container: HTMLElement) => {
  Array.from(container.getElementsByClassName("external-img-link")).forEach(
    (link: HTMLLinkElement) => {
      link.setAttribute("target", "_blank");
    }
  );
};

export function isScreenUnderWidth(widthInPx: number) {
  // Added fallback to handle errors that occur in unit test context.
  try {
    return window.matchMedia(`(max-width: ${widthInPx}px)`).matches;
  } catch (err) {
    // tslint:disable-next-line: no-console
    console.warn(
      "Error occurred calling window.matchMedia.",
      err instanceof Error && err.message
    );
    return false;
  }
}

export function useMobileWidth(widthInPx: number) {
  const [isMobileWidth, setIsMobileWidth] = React.useState(
    isScreenUnderWidth(widthInPx)
  );

  React.useEffect(() => {
    const listener = throttle(
      () => setIsMobileWidth(isScreenUnderWidth(widthInPx)),
      500
    );
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  });

  return isMobileWidth;
}

/**
 * Returns the BoundingClientRect object of the viewport (what
 * window.getBoundingClientRect() would return if that API existed)
 */
export function getViewportRect(): DOMRect {
  const html = document.documentElement;
  return makeDomRect({
    top: 0,
    bottom: html.clientHeight,
    left: 0,
    right: html.clientWidth,
  });
}

/**
 * Returns the intersection rectangle of two DOMRect objects, typically
 * generated with element.getBoundingClientRect()
 */
export function getIntersectionRect(rect1: DOMRect, rect2: DOMRect): DOMRect {
  return makeDomRect({
    top: Math.max(rect1.top, rect2.top),
    bottom: Math.min(rect1.bottom, rect2.bottom),
    left: Math.max(rect1.left, rect2.left),
    right: Math.min(rect1.right, rect2.right),
  });
}

/**
 * Whether the first rectangle is contained entirely within the second
 * rectangle. DOMRect instances are generated using
 * element.getBoundingClientRect()
 */
export function containsRect(rect1: DOMRect, rect2: DOMRect) {
  return (
    rect1.top >= rect2.top &&
    rect1.left >= rect2.left &&
    rect1.right <= rect2.right &&
    rect1.bottom <= rect2.bottom
  );
}

// The next three functions are all from
// https://stackoverflow.com/questions/123999/how-can-i-tell-if-a-dom-element-is-visible-in-the-current-viewport/

/**
 * Tests whether the element's bounds are at least partially visible within
 * the current viewport
 */
export function isElementPartiallyInViewport(el: Element) {
  const rect = el.getBoundingClientRect();
  const windowHeight =
    window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  const vertInView = rect.top <= windowHeight && rect.top + rect.height >= 0;
  const horInView = rect.left <= windowWidth && rect.left + rect.width >= 0;

  return vertInView && horInView;
}

/**
 * Tests whether the element's bounds are entirely visible within the
 * current viewport
 */
export function isElementCompletelyInViewport(el: Element) {
  const rect = el.getBoundingClientRect();
  const windowHeight =
    window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  return (
    rect.left >= 0 &&
    rect.top >= 0 &&
    rect.left + rect.width <= windowWidth &&
    rect.top + rect.height <= windowHeight
  );
}

/**
 * Tests whether the element is at least partially inside the viewport and
 * whether any part of it is visible to the user (not overlapped, etc)
 */
export function isElementVisible(el: Element) {
  const rect = el.getBoundingClientRect();
  const vWidth = window.innerWidth || document.documentElement.clientWidth;
  const vHeight = window.innerHeight || document.documentElement.clientHeight;
  const efp = (x: number, y: number) => {
    return document.elementFromPoint(x, y);
  };

  // Return false if it's not in the viewport
  if (
    rect.right < 0 ||
    rect.bottom < 0 ||
    rect.left > vWidth ||
    rect.top > vHeight
  ) {
    return false;
  }

  // Return true if any of its four corners are visible
  return (
    el.contains(efp(rect.left, rect.top)) ||
    el.contains(efp(rect.right, rect.top)) ||
    el.contains(efp(rect.right, rect.bottom)) ||
    el.contains(efp(rect.left, rect.bottom))
  );
}

/**
 * Makes a DOMRect object from top/bottom/left/right points.
 */
export function makeDomRect({
  top,
  bottom,
  left,
  right,
}: {
  top: number;
  bottom: number;
  left: number;
  right: number;
}): DOMRect {
  const rect = {
    top,
    bottom,
    left,
    right,
    x: left,
    y: top,
    height: bottom - top,
    width: right - left,
  };

  return { ...rect, toJSON: () => rect };
}

interface ScrollOptions {
  topOffset?: number;
  smooth?: boolean;
}

export const getSiteScrollContainer = () =>
  document.getElementById("site-content");

export function scrollToOffset(
  topOffset: number,
  container: Element | null = getSiteScrollContainer(),
  options?: ScrollOptions
) {
  if (!container) {
    return;
  }
  if (options?.smooth) {
    try {
      container.scroll({
        top: topOffset - (options?.topOffset ?? 0),
        left: container.scrollLeft,
        behavior: "smooth",
      });
      return;
    } finally {
    }
  }
  container.scrollTop = topOffset;
}
export function scrollToElement(
  target: Element | null,
  container: Element | null = getSiteScrollContainer(),
  options?: ScrollOptions
) {
  if (!container || !target) {
    return;
  }

  const topOffset =
    options?.topOffset ??
    (container === getSiteScrollContainer() ? TOP_NAV_HEIGHT : 0);
  const scrollTop =
    target.getBoundingClientRect().top + container.scrollTop - topOffset;
  if (options?.smooth) {
    try {
      container.scroll({
        top: scrollTop,
        left: container.scrollLeft,
        behavior: "smooth",
      });
      return;
    } finally {
    }
  }
  container.scrollTop = scrollTop;
}

export const checkVisibilityAndScroll = <TElement extends HTMLElement>(
  ref: React.RefObject<TElement>,
  topOffset?: number
) => {
  if (ref.current !== null) {
    if (!isElementCompletelyInViewport(ref.current)) {
      scrollToElement(ref.current, getSiteScrollContainer(), { topOffset });
    }
  }
};

export function scrollToId(
  id: string | number,
  container: Element | null = getSiteScrollContainer(),
  options?: ScrollOptions
) {
  scrollToElement(document.getElementById(String(id)), container, options);
}

export function scrollToHash(
  container: Element | null = getSiteScrollContainer(),
  options?: ScrollOptions
) {
  scrollToId(window.location.hash.slice(1), container, options);
}

export const copyToClipboard = (text: string) => {
  const copyFunction = (e: ClipboardEvent) => {
    (e.clipboardData as DataTransfer).setData("text/plain", text);
    e.preventDefault();
    document.removeEventListener("copy", copyFunction);
  };

  document.addEventListener("copy", copyFunction);
  document.execCommand("copy");
};

export function pathnameToPathArray(pathname: string) {
  return pathname
    .split("/")
    .filter(p => !!p)
    .map(e => e.toLowerCase());
}
